import { debugConsole } from "utilz/helpers";

const SESSION_KEY = "ai_home_session";
const FIRST_VISIT_KEY = "ai_home_first_visit";

export interface AiHomeSession {
  id: string;
  startedAt: number;
  queryCount: number;
  chatsOpened: number;
  lastAction: string | null;
  endedReported: boolean;
  lastQueryId: string | null;
}

export interface RecordedQuery {
  queryIndex: number;
  isFirstQuery: boolean;
}

let memorySession: AiHomeSession | null = null;

const createSession = (): AiHomeSession => ({
  id: crypto.randomUUID(),
  startedAt: Date.now(),
  queryCount: 0,
  chatsOpened: 0,
  lastAction: null,
  endedReported: false,
  lastQueryId: null,
});

const isStoredSession = (value: unknown): value is AiHomeSession =>
  typeof value === "object" &&
  value !== null &&
  typeof (value as AiHomeSession).id === "string" &&
  typeof (value as AiHomeSession).startedAt === "number" &&
  typeof (value as AiHomeSession).queryCount === "number";

const readSession = (): AiHomeSession | null => {
  try {
    const stored = sessionStorage.getItem(SESSION_KEY);
    if (!stored) return null;

    const parsed: unknown = JSON.parse(stored);
    if (!isStoredSession(parsed)) return null;

    memorySession = parsed;
    return parsed;
  } catch (error) {
    debugConsole("Error reading the AI home analytics session:", error);
    return memorySession;
  }
};

const writeSession = (session: AiHomeSession): AiHomeSession => {
  memorySession = session;
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
  } catch (error) {
    debugConsole("Error persisting the AI home analytics session:", error);
  }
  return session;
};

export const getAiHomeSession = (): AiHomeSession =>
  readSession() ?? writeSession(createSession());

export const aiHomeSessionId = (): string => getAiHomeSession().id;

export const timeInSessionMs = (): number =>
  Date.now() - getAiHomeSession().startedAt;

export const recordQuery = (queryId: string): RecordedQuery => {
  const session = getAiHomeSession();
  const queryIndex = session.queryCount;

  writeSession({
    ...session,
    queryCount: queryIndex + 1,
    lastAction: "query_submitted",
    lastQueryId: queryId,
  });

  return {
    queryIndex,
    isFirstQuery: queryIndex === 0,
  };
};

export const recordQueryOutcome = ({
  queryId,
  failed,
}: {
  queryId: string;
  failed: boolean;
}) => {
  const session = getAiHomeSession();
  if (session.lastQueryId !== queryId) return;

  writeSession({
    ...session,
    lastAction: failed ? "response_failed" : "response_received",
  });
};

export const recordChatOpened = () => {
  const session = getAiHomeSession();
  writeSession({
    ...session,
    chatsOpened: session.chatsOpened + 1,
    lastAction: "chat_opened",
  });
};

export const recordAction = (action: string) => {
  writeSession({ ...getAiHomeSession(), lastAction: action });
};

export const markAiHomeSessionEnded = (): boolean => {
  const session = getAiHomeSession();
  if (session.endedReported) return false;

  writeSession({ ...session, endedReported: true });
  return true;
};

export const armAiHomeSessionEnd = () => {
  const session = getAiHomeSession();
  if (!session.endedReported) return;

  writeSession({ ...session, endedReported: false });
};

export const isFirstAiHomeVisit = (): boolean => {
  try {
    if (localStorage.getItem(FIRST_VISIT_KEY)) return false;

    localStorage.setItem(FIRST_VISIT_KEY, new Date().toISOString());
    return true;
  } catch (error) {
    debugConsole("Error reading the AI home first-visit flag:", error);
    return false;
  }
};
